import type { MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'

import {
  SHIPMENT_STAGES,
  STAGE_ROUTE,
  useOpenShipmentDetail,
} from '#/entities/shipment'
import type { ShipmentStage } from '#/entities/shipment'
import { Tag } from '#/shared/ui/tag'
import type { ShipmentListCardRow } from '../model/types'

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  )
}

function CodeLink({
  shipmentId,
  code,
  stage,
}: {
  shipmentId: string
  code: string
  stage: ShipmentStage
}) {
  const openShipmentDetail = useOpenShipmentDetail()
  const route = STAGE_ROUTE[stage]
  const params = { [route.paramKey]: shipmentId }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Keep the card's row click (sidebar) from firing when the id link is used.
    event.stopPropagation()
    if (isModifiedClick(event)) return
    event.preventDefault()
    void openShipmentDetail({ shipmentId, to: route.to, params })
  }

  return (
    <Link
      to={route.to as never}
      params={params as never}
      search={((prev: Record<string, unknown>) => prev) as never}
      onClick={handleClick}
      className="shrink-0 text-sm font-medium text-primary hover:underline"
    >
      {code}
    </Link>
  )
}

export function CardHeader({
  row,
  stage,
}: {
  row: ShipmentListCardRow
  stage: ShipmentStage
}) {
  // The quote design intentionally omits the right-hand status chip.
  const showStatus = stage !== SHIPMENT_STAGES.QUOTE

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2.5">
        <CodeLink shipmentId={row.shipmentId} code={row.code} stage={stage} />
        {row.providerBadges?.map((badge, i) => (
          <Tag key={`${badge.label}-${i}`} variant={badge.variant ?? 'muted'}>
            {badge.label}
          </Tag>
        ))}
      </div>
      {showStatus && row.statusLabel && (
        <Tag variant="muted" className="shrink-0">
          {row.statusLabel}
        </Tag>
      )}
    </div>
  )
}
